import React, {useEffect, useRef, useState} from "react";
import SpeechRecognition, {useSpeechRecognition} from "react-speech-recognition";
import axios from "axios";
import Swal from "sweetalert2";
import {Button, Card, Form, InputGroup, OverlayTrigger, Spinner, Tooltip} from "react-bootstrap";
import AIAvatar from "./AIAvatar";
import ChatWindow from "./ChatWindow";
import PatientSummary from "./PatientSummary";
import LanguageSelector from "./LanguageSelector";
import {baseUrl, ipAddress, userLocation, userName} from "../Univ";
import "./AIRegister.css";
import AIIdentityCenter from "./AIIdentityCenter";
export default function AIRegister() {
    // =====================================================
    // DEFAULT PATIENT
    // =====================================================
    const emptyPatient = {
        pat_id: "", patient: "", gender: "", dob: "", age: "", mobile: "", address1: "", country: "India",
    };
    // =====================================================
    // AI STATES
    // =====================================================
    const [language, setLanguage] = useState("");
    const [registrationMode, setRegistrationMode] = useState("");
    const [conversationId, setConversationId] = useState("");
    const conversationRef = useRef("");
    const [loading, setLoading] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [voiceEnabled, setVoiceEnabled] = useState(true);
    const [speaking, setSpeaking] = useState(false);
    const languageRef = useRef("en");
    const chatContainerRef = useRef(null);
    const micEnabledRef = useRef(false);
    const silenceTimerRef = useRef(null);
    const speakingRef = useRef(false);
    // =====================================================
    // CHAT
    // =====================================================
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState([{
        sender: "bot",
        text: "👋 Welcome to DrHiS Hospital.\n\nI am your AI Receptionist.\n\nPlease choose your preferred language."
    }]);
    // =====================================================
    // PATIENT
    // =====================================================
    const [patient, setPatient] = useState(emptyPatient);
    // =====================================================
    // REFS
    // =====================================================
    const {
        transcript, listening, resetTranscript, browserSupportsSpeechRecognition
    } = useSpeechRecognition();
    const chatBottomRef = useRef(null);
    const textInputRef = useRef(null);
    // =====================================================
    // CLEAN SPEECH
    // =====================================================
    const cleanSpeech = (text) => {
        if (!text) return "";
        return text
            .replace(/[📋📦📂📑📄]/g, "")
            .replace(/\n+/g, ". ")
            .replace(/\s+/g, " ")
            .replace(/[*#]/g, "")
            .trim();
    };
    // =====================================================
    // SPEAK
    // =====================================================
    const speak = (text) => {
        if (!voiceEnabled || !text) return;
        const cleanText = cleanSpeech(text);
        if (!cleanText) return;
        // Stop anything currently speaking
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(cleanText);
        utterance.rate = 1;
        utterance.pitch = 1;
        utterance.volume = 1;
        if (languageRef.current === "ta") {
            utterance.lang = "ta-IN";
        } else if (languageRef.current === "hi") {
            utterance.lang = "hi-IN";
        } else {
            utterance.lang = "en-IN";
        }
        utterance.onstart = () => {
            console.log("TTS STARTED:", cleanText);
            speakingRef.current = true;
            setSpeaking(true);
            stopListening();
        };
        utterance.onend = () => {
            console.log("TTS ENDED");
            speakingRef.current = false;
            setSpeaking(false);
            // Automatically restart microphone
            if (micEnabledRef.current && !completed && !loading) {
                setTimeout(() => {
                    startListening();
                }, 500);
            }
        };
        utterance.onerror = (event) => {
            console.log("TTS ERROR:", event);
            speakingRef.current = false;
            setSpeaking(false);
            // Even if TTS fails, allow microphone to continue
            if (micEnabledRef.current && !completed && !loading) {
                setTimeout(() => {
                    startListening();
                }, 500);
            }
        };
        // Important for Chrome
        window.speechSynthesis.resume();
        setTimeout(() => {
            window.speechSynthesis.speak(utterance);
        }, 100);
    };
    // =====================================================
    // STOP SPEECH
    // =====================================================
    const stopSpeaking = () => {
        speakingRef.current = false;
        window.speechSynthesis.cancel();
        setSpeaking(false);
    };
    // =====================================================
// START LISTENING
// =====================================================
    const startListening = async () => {
        if (!micEnabledRef.current) return;
        if (loading) return;
        if (speakingRef.current) return;
        if (listening) return;
        resetTranscript();
        try {
            await SpeechRecognition.startListening({
                continuous: true,
                interimResults: false,
                language: languageRef.current === "ta" ? "ta-IN" : languageRef.current === "hi" ? "hi-IN" : "en-IN"
            });
        } catch (err) {
            console.log("Speech recognition start error:", err);
        }
    };
// =====================================================
// STOP LISTENING
// =====================================================
    const stopListening = async () => {
        try {
            await SpeechRecognition.stopListening();
        } catch (e) {
            console.log("Speech recognition stop error:", e);
        }
    };
// =====================================================
// MICROPHONE BUTTON
// =====================================================
    const micClicked = () => {
        if (micEnabledRef.current) {
            // TURN MIC OFF
            micEnabledRef.current = false;
            stopListening();
        } else {
            // TURN MIC ON
            micEnabledRef.current = true;
            startListening();
        }
    };
// =====================================================
// AUTOMATIC MIC RESTART
// =====================================================
    useEffect(() => {
        if (!micEnabledRef.current) return;
        if (speakingRef.current) return;
        if (loading) return;
        if (completed) return;
        if (listening) return;
        const timer = setTimeout(() => {
            startListening();
        }, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [listening, loading, completed, speaking]);
// =====================================================
// CLEANUP
// =====================================================
    useEffect(() => {
        return () => {
            micEnabledRef.current = false;
            stopListening();
            stopSpeaking();
        };
    }, []);
    // =====================================================
    // INITIALIZE SPEECH RECOGNITION
    // =====================================================
    useEffect(() => {
        if (!transcript) return;
        setMessage(transcript);
        if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
        }
        silenceTimerRef.current = setTimeout(() => {
            stopListening();
            sendMessage(transcript);
            resetTranscript();
            setMessage("");
        }, 2000);
        return () => {
            if (silenceTimerRef.current) {
                clearTimeout(silenceTimerRef.current);
            }
        };
    }, [transcript]);
    // =====================================================
    // START REGISTRATION
    // =====================================================
    const startRegistration = async (lang) => {
        try {
            setLoading(true);
            languageRef.current = lang;
            setLanguage(lang);
            const response = await axios.post(baseUrl + "ai-registration/start/", {
                language: languageRef.current
            });
            if (!response.data.success) {
                throw new Error("Unable to start.");
            }
            conversationRef.current = response.data.conversation_id;
            setConversationId(response.data.conversation_id);
            console.log("Conversation ID =", conversationRef.current);
            setPatient(response.data.patient);
            setMessages([{
                sender: "bot", text: response.data.reply
            }]);
            try {
                speak(response.data.reply);
            } catch (ttsError) {
                console.warn("DrHiS Registration TTS failed:", ttsError);
            }
            micEnabledRef.current = true;
        } catch (e) {
            console.log(e);
            Swal.fire({
                icon: "error", title: "AI Registration", text: "Unable to start registration."
            });
        } finally {
            setLoading(false);
        }
    };
// =====================================================
// SEND MESSAGE
// =====================================================
    const sendMessage = async (voiceMessage = null) => {
        const question = typeof voiceMessage === "string" ? voiceMessage : message;
        if (!question.trim()) return;
        setMessages(prev => [...prev, {
            sender: "user", text: question
        }]);
        setTimeout(() => {
            chatBottomRef.current?.scrollIntoView({
                behavior: "smooth"
            });
        }, 50);
        if (!voiceMessage) {
            setMessage("");
        }
        setLoading(true);
        try {
            const response = await axios.post(baseUrl + "ai-registration/chat/", {
                conversation_id: conversationRef.current, message: question, language: languageRef.current
            });
            console.log(language);
            if (!response.data.success) {
                throw new Error("Chat Failed");
            }
            // ===============================
            // UPDATE PATIENT SUMMARY
            // ===============================
            if (response.data.patient) {
                console.log("PATIENT UPDATE", response.data.patient);
                setPatient(response.data.patient);
            }
            setMessages(prev => [...prev, {
                sender: "bot", text: response.data.reply
            }]);
            setTimeout(() => {
                chatBottomRef.current?.scrollIntoView({
                    behavior: "smooth"
                });
            }, 50);
            speak(response.data.reply);
            // ===============================
            // COMPLETED
            // ===============================
            if (response.data.completed === true) {
                console.log("REGISTRATION COMPLETED");
                await savePatient();
            }
        } catch (error) {
            console.log("AI CHAT ERROR", error);
            setMessages(prev => [...prev, {
                sender: "bot", text: "❌ Sorry, I couldn't process your request."
            }]);
        } finally {
            setLoading(false);
        }
    };
    // =====================================================
    // SELECT LANGUAGE
    // =====================================================
    const selectLanguage = (lang) => {
        if (loading) return;
        startRegistration(lang);
    };
    const savePatient = async () => {
        try {
            setLoading(true);
            const response = await axios.post(baseUrl + "ai-registration/save/", {
                conversation_id: conversationRef.current,
                addedby: userName || "AI",
                ip_address: ipAddress,
                location: userLocation || 1
            });
            if (response.data.success) {
                Swal.fire({
                    icon: "success", title: "Registration Completed", html: `
        <div style="text-align:left">

            <div style="margin-bottom:10px">
                <b>Patient ID:</b>
                ${response.data.pat_id}
            </div>

            <div style="margin-bottom:10px">
                <b>UPID:</b>
                ${response.data.upid}
            </div>

            <div>
                <b>Master Patient ID:</b>
                ${response.data.master_patient_id}
            </div>

        </div>
    `, timer: 2000, showConfirmButton: false
                });
                setTimeout(() => {
                    resetAll();
                    micEnabledRef.current = false;
                }, 4000);
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Save Failed",
                    text: JSON.stringify(response.data.errors || response.data.message)
                });
            }
        } catch (error) {
            console.log("SAVE ERROR", error);
            Swal.fire({
                icon: "error", title: "Error", text: "Unable to save patient."
            });
        } finally {
            setLoading(false);
        }
    };
    // =====================================================
    // RESET REGISTRATION
    // =====================================================
    const resetAll = async () => {
        try {
            if (conversationId) {
                await axios.post(baseUrl + "ai-registration/reset/", {
                    conversation_id: conversationId
                });
            }
        } catch (e) {
            console.log(e);
        }
        stopListening();
        stopSpeaking();
        setConversationId("");
        setLanguage("");
        setCompleted(false);
        setLoading(false);
        setPatient(emptyPatient);
        setMessage("");
        setMessages([{
            sender: "bot",
            text: "👋 Welcome to DrHiS Hospital.\n\nI am your AI Receptionist.\n\nPlease choose your preferred language."
        }]);
    };
    // =====================================================
    // NEW REGISTRATION
    // =====================================================
    const newRegistration = () => {
        Swal.fire({
            title: "Start New Registration?", icon: "question", showCancelButton: true, confirmButtonText: "Start"
        }).then((result) => {
            if (result.isConfirmed) {
                resetAll();
                micEnabledRef.current = false;
            }
        });
    };
    // =====================================================
    // EXIT
    // =====================================================
    const exitRegistration = () => {
        Swal.fire({
            title: "Exit Registration?",
            text: "Current registration will be cancelled.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Exit"
        }).then((result) => {
            if (result.isConfirmed) {
                window.location.href = "/";
            }
        });
    };
    // =====================================================
    // VOICE
    // =====================================================
    const toggleVoice = () => {
        if (voiceEnabled) {
            stopSpeaking();
        }
        setVoiceEnabled(!voiceEnabled);
    };
    // =====================================================
    // ENTER KEY
    // =====================================================
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            sendMessage();
        }
    };
    // =====================================================
    // AUTO SAVE
    // =====================================================
    useEffect(() => {
        if (completed) {
            savePatient();
        }
    }, [completed]);
    useEffect(() => {
        const container = chatContainerRef.current;
        if (!container) return;
        container.scrollTo({
            top: container.scrollHeight, behavior: "smooth"
        });
    }, [messages]);
    // =====================================================
    // PAGE TITLE
    // =====================================================
    useEffect(() => {
        document.title = "AI Registration";
    }, []);
    // =====================================================
    // RENDER
    // =====================================================
    return (<div className="container-fluid py-3">

        <div className="row g-3">

            {/* ============================================
                    LEFT PANEL
            ============================================ */}

            <div className="col-lg-8">

                <Card className="shadow-lg border-0 rounded-4">

                    <Card.Body>

                        {/* ======================================
                                AVATAR
                        ====================================== */}

                        <div className="text-center mb-3">

                            <AIAvatar
                                listening={listening}
                                speaking={speaking}
                                processing={loading}
                            />

                        </div>

                        {/* ======================================
                                STEP 1
                                SELECT REGISTRATION MODE
                        ====================================== */}

                        {!registrationMode && (<Card className="shadow-sm">

                            <Card.Body className="text-center">

                                <h4 className="mb-4">

                                    Choose Registration Method

                                </h4>

                                <Button
                                    className="m-2"
                                    size="lg"
                                    variant="primary"
                                    onClick={() => setRegistrationMode("voice")}
                                >
                                    🎤 Voice Registration
                                </Button>

                                <Button
                                    className="m-2"
                                    size="lg"
                                    variant="success"
                                    onClick={() => setRegistrationMode("identity")}
                                >
                                    🪪 Identity Scan
                                </Button>

                            </Card.Body>

                        </Card>)}

                        {/* ======================================
                                STEP 2
                                VOICE -> LANGUAGE
                        ====================================== */}

                        {registrationMode === "voice" && !language && (<div className="mt-3">

                            <LanguageSelector
                                language={language}
                                onSelect={selectLanguage}
                            />

                        </div>)}

                        {/* ======================================
                                STEP 3
                                VOICE CHAT
                        ====================================== */}

                        {registrationMode === "voice" && language && (<>

                            <div
                                className="alert alert-light border rounded-3 d-flex justify-content-between align-items-center">

                                <div>

                                    <strong>

                                        AI Receptionist

                                    </strong>

                                </div>

                                <div>

                                    {loading && (<span className="badge bg-warning">

                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    className="me-2"
                                                />

                                                Thinking...

                                            </span>)}

                                    {!loading && speaking && (<span className="badge bg-success">

                                                🔊 Speaking

                                            </span>)}

                                    {!loading && !speaking && listening && (<span className="badge bg-primary">

                                                🎤 Listening

                                            </span>)}

                                    {!loading && !speaking && !listening && (<span className="badge bg-secondary">

                                                Ready

                                            </span>)}

                                </div>

                            </div>

                            <div
                                ref={chatContainerRef}
                                className="mt-3"
                                style={{
                                    height: "470px", overflowY: "auto", paddingRight: "8px"
                                }}
                            >

                                <ChatWindow
                                    messages={messages}
                                />

                                <div ref={chatBottomRef}></div>

                            </div>

                            <div className="mt-4">

                                <InputGroup>

                                    <Form.Control
                                        ref={textInputRef}
                                        placeholder="Type your message..."
                                        value={message}
                                        disabled={loading}
                                        autoComplete="off"
                                        onChange={(e) => setMessage(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                    />

                                    <Button
                                        variant="primary"
                                        disabled={loading}
                                        onClick={() => sendMessage()}
                                    >
                                        <i className="fa fa-paper-plane"></i>
                                    </Button>

                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Voice Input</Tooltip>}
                                    >

                                        <Button
                                            variant={listening ? "danger" : "success"}
                                            disabled={loading}
                                            onClick={micClicked}

                                        >

                                            <i className="fa fa-microphone"></i>

                                        </Button>

                                    </OverlayTrigger>

                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Toggle Voice</Tooltip>}
                                    >

                                        <Button
                                            variant={voiceEnabled ? "info" : "secondary"}
                                            onClick={toggleVoice}
                                        >

                                            <i
                                                className={voiceEnabled ? "fa fa-volume-up" : "fa fa-volume-off"}
                                            />

                                        </Button>

                                    </OverlayTrigger>

                                </InputGroup>

                            </div>

                        </>)}

                        {/* ======================================
                                IDENTITY SCAN
                        ====================================== */}

                        {registrationMode === "identity" && (<AIIdentityCenter
                            patient={patient}
                            setPatient={setPatient}
                            savePatient={savePatient}
                        />)}

                    </Card.Body>

                </Card>

            </div>

            {/* ============================================
                    RIGHT PANEL
            ============================================ */}

            <div className="col-lg-4">

                <Card className="shadow-lg border-0 rounded-4 mb-3">

                    <Card.Header className="bg-primary text-white">

                        <strong>

                            Patient Summary

                        </strong>

                    </Card.Header>

                    <Card.Body>

                        <PatientSummary
                            patient={patient}
                        />

                    </Card.Body>

                </Card>

                <Card className="shadow border-0 rounded-4">

                    <Card.Body className="d-grid gap-2">

                        <Button
                            variant="success"
                            size="lg"
                            onClick={newRegistration}
                        >

                            <i className="fa fa-plus-circle me-2"></i>

                            New Registration

                        </Button>

                        {registrationMode === "voice" && language && (<Button
                            variant="secondary"
                            size="lg"
                            onClick={toggleVoice}
                        >

                            <i
                                className={voiceEnabled ? "fa fa-volume-up me-2" : "fa fa-volume-off me-2"}
                            />

                            {voiceEnabled ? "Disable Voice" : "Enable Voice"}

                        </Button>)}

                        <Button
                            variant="danger"
                            size="lg"
                            onClick={exitRegistration}
                        >

                            <i className="fa fa-times-circle me-2"></i>

                            Exit

                        </Button>

                    </Card.Body>

                </Card>

            </div>

        </div>

    </div>);
}